import React, { Component } from 'react'
import { Modal, Select, Form, Input, Upload, Icon, message } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import LangContext from 'modules/shared/context/langContext'
import * as api from 'modules/logistic-command-center/alert-dashboard/components/api'
import { findEventSafety } from 'modules/logistic-command-center/alert-dashboard/components/utils'

const FormItem = Form.Item

const REASON_IDS_SUGGEST_STOP_POINT = [50, 51, 55, 56]

const getBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })

class LccCauseModal extends Component {
  static contextType = LangContext

  state = {
    reasons: [],
    eventSafties: [],
    selectedReason: null,
    fileList: [],
    previewVisible: false,
    previewImage: '',
    isLoading: false,
    isSaving: false,
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.onOpen()
    }
  }

  onOpen = async () => {
    if (this.props.token) {
      api.createAxios(this.props.token)
    }
    try {
      this.setState({ isLoading: true })
      const [reasons, eventSafties] = await Promise.all([
        api.loadLccReason(),
        api.loadEventSafty(),
      ])
      this.setState({ reasons, eventSafties })
    } catch (e) {
      message.error(this.context.f.failedToLoadInitialData)
    } finally {
      this.setState({ isLoading: false })
    }
  }

  getFilteredReasons = () => {
    const { record } = this.props
    const { reasons, eventSafties } = this.state

    if (!record || !eventSafties || eventSafties.length === 0) {
      return {
        visibleReasons: reasons.filter(r => r.id > 0),
        hiddenReasons: [],
      }
    }

    const eventSafty = findEventSafety(
      eventSafties,
      record.eventType,
      record.alarmType,
    )
    const lccReasons = eventSafty ? eventSafty.lcc_reasons || [] : []

    if (lccReasons.length > 0) {
      const excludeIds = new Set(lccReasons.map(item => item.id))
      const hiddenReasons = reasons.filter(item => !excludeIds.has(item.id))
      return { visibleReasons: lccReasons, hiddenReasons }
    }

    return { visibleReasons: reasons.filter(r => r.id > 0), hiddenReasons: [] }
  }

  validateFile = file => {
    const isValidType = ['image/jpeg', 'image/png'].includes(file.type)
    const isUnder2MB = file.size / 1024 <= 2048
    if (!isValidType) {
      message.error(this.context.y.youCanOnlyUploadJPGPNG)
      return false
    }
    if (!isUnder2MB) {
      message.error(this.context.f.fileMustSmallerThan2MB)
      return false
    }
    return true
  }

  handlePreview = async file => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj)
    }
    this.setState({
      previewImage: file.url || file.preview,
      previewVisible: true,
    })
  }

  handleCancelPreview = () => this.setState({ previewVisible: false })

  showConfirm = (payload, values, resetForm) => {
    const { onOk } = this.props
    const { selectedReason } = this.state

    Modal.confirm({
      title: this.context.c.confirm,
      okText: this.context.c.confirm,
      cancelText: this.context.c.cancel,
      onOk: async () => {
        this.setState({ isSaving: true })
        try {
          const { files } = values
          const file_url =
            files && files.length > 0
              ? files[0] instanceof File
                ? await api.uploadFile(files)
                : files.map(file => {
                    if (typeof file === 'string') return file
                    return file.url
                  })
              : []

          await api.updateLccCause({ ...payload, file_url })

          onOk({
            reason: selectedReason,
            note: values.note,
            files: values.files,
          })
          resetForm()
          this.setState({ selectedReason: null, fileList: [], isSaving: false })
        } catch (err) {
          this.setState({ isSaving: false })
          message.error((err && err.message) || this.context.c.editFail)
        }
      },
    })
  }

  handleSave = (values, resetForm) => {
    const { record, auth } = this.props
    const { selectedReason } = this.state
    const profile = auth ? auth.profile : {}
    const {
      id: userId = 0,
      username = '',
      firstname = '',
      lastname = '',
    } = profile

    let incident_lcc_status = record.is_transporter_enable
      ? selectedReason && selectedReason.is_violation === 1
        ? 3
        : 2
      : 3

    const updatedValue = {
      ...selectedReason,
      note: values.note,
      updated_at: moment(),
      updated_by: { id: userId, username, firstname, lastname },
    }

    const payload = {
      id: record.id,
      value: updatedValue,
      incident_lcc_status,
      is_violation: selectedReason ? selectedReason.is_violation : 0,
      file_url: [],
    }

    this.showConfirm(payload, values, resetForm)
  }

  handleClose = (resetForm, onCancel) => {
    resetForm()
    this.setState({
      selectedReason: null,
      fileList: [],
      previewVisible: false,
    })
    onCancel()
  }

  render() {
    const { visible, onCancel, onOk } = this.props
    const {
      selectedReason,
      fileList,
      previewVisible,
      previewImage,
      isLoading,
      isSaving,
    } = this.state

    const { visibleReasons, hiddenReasons } = this.getFilteredReasons()

    const showNote = selectedReason && selectedReason.id === 27
    const showFile = selectedReason !== null

    const uploadButton = (
      <LangContext.Consumer>
        {i18n => (
          <div>
            <Icon type="plus" />
            <div className="ant-upload-text">{i18n.u.upload}</div>
          </div>
        )}
      </LangContext.Consumer>
    )

    return (
      <Formik
        initialValues={{ note: '', files: [] }}
        enableReinitialize
        validationSchema={Yup.object().shape({
          files: Yup.array().max(1, 'Only 1 file allowed'),
        })}
        onSubmit={(values, { resetForm }) => {
          this.handleSave(values, resetForm)
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldValue,
          resetForm,
        }) => (
          <Modal
            visible={visible}
            title={
              <LangContext.Consumer>
                {i18n => i18n.r.recordTheReason}
              </LangContext.Consumer>
            }
            onCancel={() => this.handleClose(resetForm, onCancel)}
            onOk={handleSubmit}
            okButtonProps={{ disabled: !selectedReason || isSaving }}
            confirmLoading={isLoading || isSaving}
            bodyStyle={{ minHeight: 400 }}
          >
            {/* Section 1 — เลือกสาเหตุ */}
            <FormItem
              label={
                <LangContext.Consumer>
                  {i18n => i18n.r.reason}
                </LangContext.Consumer>
              }
            >
              <Select
                style={{ width: '100%' }}
                value={selectedReason ? selectedReason.id : null}
                showSearch
                loading={isLoading}
                filterOption={(input, item) =>
                  item.props.children
                    .toUpperCase()
                    .indexOf(input.toUpperCase()) !== -1
                }
                onChange={value => {
                  const allReasons = [...visibleReasons, ...hiddenReasons]
                  const reason = allReasons.find(r => r.id === value) || null
                  this.setState({ selectedReason: reason, fileList: [] })
                  setFieldValue('note', '')
                  setFieldValue('files', [])
                }}
              >
                {visibleReasons
                  .filter(r => r.id > 0)
                  .sort((a, b) => (a.id === 27 ? 1 : 0) - (b.id === 27 ? 1 : 0))
                  .map(reason => (
                    <Select.Option key={reason.id} value={reason.id}>
                      {reason.name}
                    </Select.Option>
                  ))}
                {hiddenReasons.map(reason => (
                  <Select.Option
                    key={reason.id}
                    value={reason.id}
                    style={{ display: 'none' }}
                  >
                    {reason.name}
                  </Select.Option>
                ))}
              </Select>
              {selectedReason &&
                REASON_IDS_SUGGEST_STOP_POINT.includes(selectedReason.id) && (
                  <div
                    style={{
                      color: '#faad14',
                      marginTop: 4,
                      lineHeight: '25px',
                    }}
                  >
                    <Icon type="info-circle" style={{ marginRight: 4 }} />
                    <LangContext.Consumer>
                      {i18n => i18n.s.shouldCreateStopPointForThisLocation}
                    </LangContext.Consumer>
                  </div>
                )}
            </FormItem>

            {/* Section 2 — ฟอร์ม (แสดงเมื่อเลือกสาเหตุแล้ว) */}
            {showNote && (
              <FormItem
                label={
                  <LangContext.Consumer>
                    {i18n => i18n.s.specifyMore}
                  </LangContext.Consumer>
                }
                validateStatus={
                  touched.note && errors.note ? 'error' : undefined
                }
                help={touched.note && errors.note}
              >
                <Input
                  name="note"
                  value={values.note}
                  onChange={handleChange}
                />
              </FormItem>
            )}

            {showFile && (
              <FormItem
                label={
                  <LangContext.Consumer>
                    {i18n => i18n.a.activeStatusImage}
                  </LangContext.Consumer>
                }
                validateStatus={
                  touched.files && errors.files ? 'error' : undefined
                }
                help={touched.files && errors.files}
              >
                <div className="clearfix">
                  <style>{`
                    .lcc-cause-upload .ant-upload.ant-upload-select-picture-card {
                      width: 150px;
                      height: 150px;
                    }
                    .lcc-cause-upload .ant-upload-list-picture-card-container {
                      width: 150px;
                      height: 150px;
                    }
                    .lcc-cause-upload .ant-upload-list-picture-card .ant-upload-list-item {
                      width: 150px;
                      height: 150px;
                    }
                  `}</style>
                  <div className="lcc-cause-upload">
                    <Upload
                      multiple
                      listType="picture-card"
                      fileList={fileList}
                      accept="image/*"
                      onPreview={this.handlePreview}
                      beforeUpload={() => false}
                      onRemove={() => {
                        setFieldValue('files', [])
                        this.setState({ fileList: [] })
                      }}
                      onChange={({ fileList: newFileList, file }) => {
                        if (
                          file.status !== 'removed' &&
                          !this.validateFile(file)
                        )
                          return

                        const newFiles =
                          file.status === 'removed'
                            ? values.files.filter(f => f.uid !== file.uid)
                            : [...values.files, file]

                        setFieldValue('files', newFiles)
                        this.setState({ fileList: newFileList })
                      }}
                    >
                      {fileList.length >= 1 ? null : uploadButton}
                    </Upload>
                  </div>

                  <Modal
                    visible={previewVisible}
                    footer={null}
                    width={800}
                    onCancel={this.handleCancelPreview}
                  >
                    <img
                      alt="preview"
                      style={{ width: '100%' }}
                      src={previewImage}
                    />
                  </Modal>
                </div>
              </FormItem>
            )}
          </Modal>
        )}
      </Formik>
    )
  }
}

export default LccCauseModal
